import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { detectLocale, setLocale, t as translate, type Locale } from "@ares/i18n";
import { getCoupledLocale } from "@ares/tokens";
import { useTenant } from "../hooks/useTenant";
import { useThemeContext } from "./ThemeProvider";

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: typeof translate;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

const STORAGE_KEY = "ares.locale";

function readStoredLocale(): Locale | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw && raw.length > 0 ? (raw as Locale) : null;
}

interface LocaleProviderProps {
  children: ReactNode;
}

export function LocaleProvider({ children }: LocaleProviderProps) {
  const { tenant } = useTenant();
  const { mode } = useThemeContext();

  const [localeState, setLocaleState] = useState<Locale>(() => {
    const stored = readStoredLocale();
    return (
      stored ??
      detectLocale({
        tenant,
        themeMode: mode,
        browserLanguage: typeof navigator !== "undefined" ? navigator.language : "en",
      })
    );
  });

  // Sync to <html lang> + dataset whenever locale changes.
  useEffect(() => {
    setLocale(localeState);
  }, [localeState]);

  // Auto-couple locale when theme switches to/from medic.
  useEffect(() => {
    if (readStoredLocale()) return; // Respect explicit user-pref.
    if (mode === "medic") {
      const coupled = getCoupledLocale(mode, tenant);
      if (coupled && coupled !== localeState) setLocaleState(coupled as Locale);
    }
  }, [mode, tenant, localeState]);

  const handleSetLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale: localeState, setLocale: handleSetLocale, t: translate }),
    [localeState, handleSetLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocaleContext(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocaleContext must be used within LocaleProvider");
  return ctx;
}
